import { fetchFromCache, FetchAPIOptions } from '@/services/api';
import { fetchFromWordpressAndCache } from '@/services/wordpress-api';
import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';

type Node = {
  id: string;
  title: string;
  content: string;
  date: string;
  slug: string;
  banner: BannerInfo;
};

export type BannerNode = Node;

type BaseBannerInfo = {
  lang: string;
  hasDoNotShowAgain: boolean;
  openedAt: string;
  closedAt: string;
  bannerBgColor: string;
};

type BannerWithTag = {
  hasTag: true;
  tagBgColor: string;
  tagContents: string;
  tagFontColor: string;
};

type BannerWithoutTag = {
  hasTag: false;
  tagBgColor?: never;
  tagContents?: never;
  tagFontColor?: never;
};

type BannerWithCta = {
  hasCta: true;
  ctaLink: string;
  ctaContentsColor: string;
  ctaContents: string;
};

type BannerWithoutCta = {
  hasCta: false;
  ctaLink?: never;
  ctaContentsColor?: never;
  ctaContents?: never;
};

export type BannerInfo = BaseBannerInfo &
  (BannerWithTag | BannerWithoutTag) &
  (BannerWithCta | BannerWithoutCta);

const GET_BANNER_QUERY = `
  query get_items($postCount: Int!) {
      posts(
        first: $postCount,
        where: {
          categoryId: ${CONTENT_CATEGORY_ID.banner},
          status: PUBLISH
        }
      ) {
          edges {
              node {
                  id
                  title
                  content
                  date
                  slug
                  banner {
                      lang
                      hasDoNotShowAgain
                      hasCta
                      ctaLink
                      ctaContentsColor
                      ctaContents
                      openedAt
                      closedAt
                      bannerBgColor
                      hasTag
                      tagBgColor
                      tagContents
                      tagFontColor
                  }
              }
          }
      }
  }`;

export type BannerSchedule = {
  id: string;
  /** epoch ms */
  openedAt: number;
  /** epoch ms, 없으면 무기한 */
  closedAt: number | null;
};

// WPGraphQL 전역 id(base64). 배너 노출 판정 CSS 선택자에 그대로 쓰므로 형식을 강제한다
const BANNER_ID_PATTERN = /^[A-Za-z0-9+/=_-]+$/;

/**
 * CMS 날짜 문자열을 epoch ms 로 정규화한다. 오픈일이 없거나 어느 날짜든 파싱할 수 없으면
 * null — 잘못 입력된 배너를 무기한 노출하지 않는다. id 형식이 예상과 달라도 null.
 */
export function getBannerSchedule(node: Node): BannerSchedule | null {
  if (!BANNER_ID_PATTERN.test(node.id)) return null;

  const openedAt = Date.parse(node.banner?.openedAt ?? '');
  if (Number.isNaN(openedAt)) return null;

  const rawClosedAt = node.banner?.closedAt;
  const closedAt = rawClosedAt ? Date.parse(rawClosedAt) : null;
  if (Number.isNaN(closedAt)) return null;

  return { id: node.id, openedAt, closedAt };
}

// 운영 빌드에서 next.config 가 인라인한 스냅샷 — 같은 값이면 다시 파싱하지 않는다.
// ⚠️ process.env.BANNER_SNAPSHOT 은 상수로 바꾸지 말 것: 빌드 인라인은 이 표기 그대로일 때만 된다
let parsedSnapshot: { raw: string; data: any } | undefined;
const readBuildSnapshot = (raw: string) => {
  if (parsedSnapshot?.raw !== raw) {
    parsedSnapshot = { raw, data: JSON.parse(raw) };
  }
  return parsedSnapshot.data;
};

/**
 * 배너 원본 데이터 출처를 고른다.
 * - 갱신(운영 반영·웹훅): WordPress 에서 읽어 S3 캐시에 올린다
 * - 운영 빌드: 빌드 시작 시 고정한 스냅샷만 쓴다(런타임 S3 조회 없음)
 * - 그 밖(개발): S3 캐시를 런타임에 읽는다
 */
async function loadBannerData(
  label: string,
  postCount: number,
  options?: FetchAPIOptions,
) {
  if (options?.refresh) {
    return fetchFromWordpressAndCache(
      GET_BANNER_QUERY,
      { variables: { postCount } },
      { label, prodFilter: options?.prodFilter },
    );
  }
  if (process.env.BANNER_SNAPSHOT !== undefined) {
    return readBuildSnapshot(process.env.BANNER_SNAPSHOT);
  }
  return fetchFromCache(label);
}

/**
 * 아직 종료되지 않은 배너 — **오픈 전 배너도 포함**한다. 지금 노출할지는 브라우저가
 * 판정한다(`banner-eligibility.ts`). 날짜·id 가 잘못된 배너는 뺀다.
 */
export async function getBannerInfo(
  postCount: number = 100,
  locale?: string,
  options?: FetchAPIOptions,
): Promise<Node[]> {
  try {
    const label = 'getBannerInfo';

    const data = await loadBannerData(label, postCount, options);

    if (!data) return [];

    const bannerInfos: Node[] = data?.posts.edges.map((edge: any) => edge.node);

    const now = Date.now();
    return bannerInfos.filter((node: Node) => {
      if (locale && node.banner.lang !== locale) return false;

      const schedule = getBannerSchedule(node);
      if (!schedule) return false;
      return schedule.closedAt === null || now < schedule.closedAt;
    });
  } catch (error) {
    // 갱신(운영 반영·웹훅)에서 조회·업로드가 실패하면 성공으로 보고하지 않도록 다시 던진다
    if (options?.refresh) throw error;
    return [];
  }
}
